import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Quote, Calendar, ArrowRight, User } from 'lucide-react';
import usePageBanner from '../hooks/usePageBanner';
import { AutoTranslate } from '../hooks/useAutoTranslate';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PriestQuoteBox = ({ t, isRTL, language }) => {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const res = await fetch(`${API_URL}/api/priest-quote`);
        if (res.ok) {
          const data = await res.json();
          setQuote(data);
        }
      } catch (error) {
        console.error('Error fetching priest quote:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuote();
  }, []);

  // Default quote if none in DB
  const defaultQuote = {
    text: { sv: "Kära församlingsbo, fastetiden har börjat. Jag önskar dig lycka till och Guds vänskap och välsignelse." },
    signature: "Kyrkoherden"
  };

  const displayQuote = quote || defaultQuote;
  
  // Get localized text
  const getLocalizedText = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field[language] || field.sv || field.en || Object.values(field)[0] || '';
  };

  const quoteText = getLocalizedText(displayQuote.text);

  if (loading) {
    return (
      <div className="animate-pulse bg-yellow-50 rounded-lg p-6 mb-8">
        <div className="h-20 bg-yellow-100 rounded"></div>
      </div>
    );
  }

  if (!quoteText) {
    return null;
  }

  return (
    <Card className="bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200 mb-8 shadow-xl card-hover" data-testid="priest-quote-box">
      <CardContent className="p-6 md:p-8">
        <div className={`flex ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Quote className={`w-10 h-10 text-yellow-600 flex-shrink-0 ${isRTL ? 'ml-4 transform scale-x-[-1]' : 'mr-4'}`} />
          <div className="flex-1">
            <p className={`text-lg md:text-xl text-gray-800 font-serif italic leading-relaxed ${isRTL ? 'text-right' : ''}`}>
              <AutoTranslate>{quoteText}</AutoTranslate>
            </p>
            <p className={`mt-4 text-yellow-700 font-semibold ${isRTL ? 'text-left' : 'text-right'}`}>
              — {displayQuote.signature || 'Kyrkoherden'}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const BlogCard = ({ article, t, isRTL, language }) => {
  const getLocalizedContent = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field[language] || field.sv || field.en || Object.values(field)[0] || '';
  };

  const title = getLocalizedContent(article.title);
  const excerpt = getLocalizedContent(article.excerpt);
  const imageUrl = article.image_url ? 
    (article.image_url.startsWith('http') ? article.image_url : `${API_URL}${article.image_url}`) 
    : null;

  const publishDate = article.published_at ? 
    new Date(article.published_at).toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    }) : '';

  return (
    <Card className="overflow-hidden hover:shadow-xl card-hover transition-shadow" data-testid={`blog-card-${article.id}`}>
      {imageUrl && (
        <div className="aspect-video overflow-hidden">
          <img 
            src={imageUrl} 
            alt={title}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
        </div>
      )}
      <CardContent className="p-5">
        <div className={`flex items-center gap-2 text-sm text-gray-500 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Calendar className="w-4 h-4" />
          <span>{publishDate}</span>
          {article.author && (
            <>
              <span className="mx-1">•</span>
              <User className="w-4 h-4" />
              <span>{article.author}</span>
            </>
          )}
        </div>
        <h3 className={`font-serif font-bold text-xl text-gray-900 mb-2 line-clamp-2 ${isRTL ? 'text-right' : ''}`}>
          <AutoTranslate>{title}</AutoTranslate>
        </h3>
        {excerpt && (
          <p className={`text-gray-600 text-sm line-clamp-3 mb-4 ${isRTL ? 'text-right' : ''}`}>
            <AutoTranslate>{excerpt}</AutoTranslate>
          </p>
        )}
        <Link 
          to={`/prastens-horna/${article.id}`}
          className={`inline-flex items-center text-yellow-700 hover:text-yellow-800 font-medium text-sm ${isRTL ? 'flex-row-reverse' : ''}`}
        >
          <AutoTranslate>{t('readMore') || 'Läs mer'}</AutoTranslate>
          <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-1 transform rotate-180' : 'ml-1'}`} />
        </Link>
      </CardContent>
    </Card>
  );
};

const PrastensHornaPage = () => {
  const { language, t, isRTL } = useLanguage();
  const { bannerImage } = usePageBanner('priest-corner', language);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await fetch(`${API_URL}/api/priest-blogs?status=published`);
        if (res.ok) {
          const data = await res.json();
          setArticles(data);
        }
      } catch (error) {
        console.error('Error fetching priest blogs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  return (
    <div className={`min-h-screen themed-page flex flex-col ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />
      
      {/* Hero Banner */}
      <div 
        className="relative h-64 md:h-80 bg-cover bg-center"
        style={{ 
          backgroundImage: bannerImage 
            ? `url(${bannerImage.startsWith('http') ? bannerImage : API_URL + bannerImage})`
            : 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)'
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="absolute inset-0 flex items-center">
          <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full ${isRTL ? 'text-right' : 'text-left'}`}>
            <div className="text-white">
              <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4">
                <AutoTranslate>{t('priestCorner') || "Prästens lilla hörna"}</AutoTranslate>
              </h1>
              <p className="text-lg md:text-xl opacity-90">
                <AutoTranslate>{t('priestCornerSubtitle') || "Tankar och reflektioner från kyrkoherden"}</AutoTranslate>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-grow bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Priest Quote */}
          <PriestQuoteBox t={t} isRTL={isRTL} language={language} />

          {/* Blog Articles */}
          <div className="mb-8">
            <h2 className={`text-2xl font-serif font-bold text-gray-900 mb-6 ${isRTL ? 'text-right' : ''}`}>
              <AutoTranslate>{t('latestPosts') || "Senaste inlägg"}</AutoTranslate>
            </h2>
            
            {loading ? (
              <div className="grid gap-6 md:grid-cols-2">
                {[1, 2].map(i => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-gray-200 aspect-video rounded-t-lg"></div>
                    <div className="bg-white p-5 rounded-b-lg">
                      <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
                      <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : articles.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-gray-500">
                  <AutoTranslate>{t('noPostsYet') || "Inga inlägg ännu. Kom tillbaka snart!"}</AutoTranslate>
                </p>
              </Card>
            ) : (
              <div className="grid gap-6 md:grid-cols-2">
                {articles.map(article => (
                  <BlogCard 
                    key={article.id} 
                    article={article} 
                    t={t} 
                    isRTL={isRTL} 
                    language={language} 
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrastensHornaPage;
